/**
 * Role関連のユーティリティ関数
 */

import { Role } from '../types';

/**
 * 有効なRole値の配列
 */
const VALID_ROLES = [
  'super_admin',
  'admin',
  'dispatcher',
  'driver',
  'land_transporter',
  'manager',
  'read_only',
] as const;

/**
 * 文字列が有効なRole値かどうかを判定するType Guard
 * @param role チェックする文字列
 * @returns 有効なRole値の場合true
 * @example
 * if (isValidRole(userRole)) {
 *   // userRoleはRole型として扱える
 *   const label = getRoleLabel(userRole);
 * }
 */
export function isValidRole(role: string): role is Role {
  return VALID_ROLES.includes(role as Role);
}

/**
 * Role enumの値を日本語ラベルに変換する
 * @param role Role enum値
 * @returns 日本語のロールラベル
 * @example
 * getRoleLabel(Role.ADMIN) // => "陸送会社管理者"
 * getRoleLabel('admin')    // => "陸送会社管理者"
 */
export const getRoleLabel = (role: Role | string): string => {
  switch (role) {
    case Role.SUPER_ADMIN:
    case 'super_admin':
      return 'ラクハイ管理者';
    case Role.ADMIN:
    case 'admin':
      return '陸送会社管理者';
    case Role.DISPATCHER:
    case 'dispatcher':
      return '配車担当者';
    case Role.DRIVER:
    case 'driver':
      return 'ドライバー';
    case Role.LAND_TRANSPORTER:
    case 'land_transporter':
      return '陸送員';
    case Role.MANAGER:
    case 'manager':
      return 'マネージャー';
    case Role.READ_ONLY:
    case 'read_only':
      return '閲覧者';
    default:
      console.warn(`Unknown role: ${role}`);
      return '不明な権限';
  }
};

/**
 * 会社ユーザー向けのRole表示ラベル（"陸送会社管理者"を"管理者"に短縮）
 * @param role Role enum値
 * @returns 日本語のロールラベル（会社向け短縮版）
 * @example
 * getCompanyRoleLabel(Role.ADMIN) // => "管理者"
 * getCompanyRoleLabel('admin')    // => "管理者"
 */
export const getCompanyRoleLabel = (role: Role | string): string => {
  switch (role) {
    case Role.ADMIN:
    case 'admin':
      return '管理者';
    case Role.DISPATCHER:
    case 'dispatcher':
      return '配車・営業担当者';
    case Role.DRIVER:
    case 'driver':
      return '陸送員';
    case Role.LAND_TRANSPORTER:
    case 'land_transporter':
      return '陸送員';
    case Role.MANAGER:
    case 'manager':
      return 'マネージャー';
    case Role.READ_ONLY:
    case 'read_only':
      return '閲覧専用';
    default:
      console.warn(`Unknown role: ${role}`);
      return '不明な権限';
  }
};

/**
 * Role enum値に対応するTailwind CSSカラークラスを取得する
 * @param role Role enum値
 * @returns Tailwind CSSカラークラス文字列
 * @example
 * getRoleColor(Role.ADMIN) // => "bg-blue-100 text-blue-800 dark:bg-blue-800/20 dark:text-blue-400"
 * getRoleColor('admin')    // => "bg-blue-100 text-blue-800 dark:bg-blue-800/20 dark:text-blue-400"
 */
export const getRoleColor = (role: Role | string): string => {
  switch (role) {
    case Role.SUPER_ADMIN:
    case 'super_admin':
      return 'bg-purple-100 text-purple-800 dark:bg-purple-800/20 dark:text-purple-400';
    case Role.ADMIN:
    case 'admin':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-800/20 dark:text-blue-400';
    case Role.DISPATCHER:
    case 'dispatcher':
      return 'bg-green-100 text-green-800 dark:bg-green-800/20 dark:text-green-400';
    case Role.DRIVER:
    case 'driver':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-800/20 dark:text-yellow-400';
    case Role.LAND_TRANSPORTER:
    case 'land_transporter':
      return 'bg-orange-100 text-orange-800 dark:bg-orange-800/20 dark:text-orange-400';
    case Role.MANAGER:
    case 'manager':
      return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-800/20 dark:text-indigo-400';
    case Role.READ_ONLY:
    case 'read_only':
      return 'bg-gray-100 text-gray-800 dark:bg-gray-800/20 dark:text-gray-400';
    default:
      console.warn(`Unknown role: ${role}`);
      return 'bg-gray-100 text-gray-800 dark:bg-gray-800/20 dark:text-gray-400';
  }
};
